/**
 * Generic form validation utilities
 */

export interface ValidationRule<T> {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: T) => string | null;
  message?: string;
}

export type ValidationRules<T> = {
  [K in keyof T]?: ValidationRule<T[K]>;
};

export type ValidationErrors<T> = {
  [K in keyof T]?: string;
};

/**
 * Creates a form validator with the given rules
 * @param rules - Validation rules for each field
 * @returns Object with validation functions
 */
export function createFormValidator<T extends Record<string, unknown>>(rules: ValidationRules<T>) {
  /**
   * Validates a single field value
   * @param field - Field name
   * @param value - Field value
   * @returns Error message or null if valid
   */
  const validateField = (field: keyof T, value: T[keyof T]): string | null => {
    const rule = rules[field];
    if (!rule) return null;

    // Required validation
    if (rule.required && (!value || (typeof value === 'string' && !value.trim()))) {
      return rule.message || `${field as string} is required`;
    }

    // Skip other validations if value is empty and not required
    if (!value || (typeof value === 'string' && !value.trim())) {
      return null;
    }

    // Min length validation
    if (rule.minLength && typeof value === 'string' && value.length < rule.minLength) {
      return rule.message || `${field as string} must be at least ${rule.minLength} characters`;
    }

    // Max length validation
    if (rule.maxLength && typeof value === 'string' && value.length > rule.maxLength) {
      return rule.message || `${field as string} must be no more than ${rule.maxLength} characters`;
    }

    // Pattern validation
    if (rule.pattern && typeof value === 'string' && !rule.pattern.test(value)) {
      return rule.message || `${field as string} format is invalid`;
    }

    // Custom validation
    if (rule.custom) {
      return rule.custom(value);
    }

    return null;
  };

  /**
   * Validates all fields in the form data
   * @param formData - Form data to validate
   * @returns Validation errors object
   */
  const validateForm = (formData: T): ValidationErrors<T> => {
    const errors: ValidationErrors<T> = {};

    for (const field in rules) {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
      }
    }

    return errors;
  };

  /**
   * Validates a single field and returns whether it's valid
   * @param field - Field name
   * @param value - Field value
   * @returns True if valid, false otherwise
   */
  const isValidField = (field: keyof T, value: T[keyof T]): boolean => {
    return validateField(field, value) === null;
  };

  /**
   * Checks if the entire form is valid
   * @param formData - Form data to validate
   * @returns True if all fields are valid, false otherwise
   */
  const isFormValid = (formData: T): boolean => {
    const errors = validateForm(formData);
    return Object.keys(errors).length === 0;
  };

  return {
    validateField,
    validateForm,
    isValidField,
    isFormValid,
  };
}

/**
 * Common validation patterns
 */
export const validationPatterns = {
  email: /^\S+@\S+\.\S+$/,
  phone: /^[+]?[1-9][\d]{0,15}$/,
  confirmationCode: /^\d{4,6}$/,
  name: /^[a-zA-Z\s'-]+$/,
};

/**
 * Common validation rules
 */
export const commonRules = {
  required: (message?: string): ValidationRule<unknown> => ({
    required: true,
    message: message || 'This field is required',
  }),
  
  email: (message?: string): ValidationRule<string> => ({
    required: true,
    pattern: validationPatterns.email,
    message: message || 'Please enter a valid email address',
  }),
  
  minLength: (min: number, message?: string): ValidationRule<string> => ({
    minLength: min,
    message: message || `Must be at least ${min} characters`,
  }),
  
  maxLength: (max: number, message?: string): ValidationRule<string> => ({
    maxLength: max,
    message: message || `Must be no more than ${max} characters`,
  }),
  
  confirmationCode: (message?: string): ValidationRule<string> => ({
    required: true,
    pattern: validationPatterns.confirmationCode,
    message: message || 'Code must be 4-6 digits',
  }),
};
